import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './index.css';
import { questions, gcseQuestions, envSciQuestions } from './data/questions';
import { emptySteps } from './config/archetypes';
import Sidebar from './components/Sidebar';
import AnswerPanel from './components/AnswerPanel';
import FeedbackTerminal from './components/FeedbackTerminal';
import ResourceViewer from './components/ResourceViewer';
import Login from './components/Login';
import UsageDashboard from './components/UsageDashboard';
import MockExamOrchestrator from './components/MockExam/MockExamOrchestrator';
import ExamHistoryOrchestrator from './components/ExamHistory/ExamHistoryOrchestrator';
import HistoryHub from './components/HistoryHub';
import QuizOrchestrator from './components/Quiz/QuizOrchestrator';
import Leaderboard from './components/Leaderboard';
import { ScoreCenter, ScoreHistory } from './components/ScoreReview';
import confetti from 'canvas-confetti';
import { markAnswer } from './services/GeminiService';
import { markOutline4, markAssess6, markScaffold9, markScaffold9Breakdown, markEssay20, markEssay20Breakdown } from './services/AnthropicService';

const EMPTY_STEPS = {
  summary: '',
  point1: '',
  point2: '',
  conclusion: '',
};

const EMPTY_FEEDBACKS = { gemini: null, claude: null };
const EMPTY_ERRORS    = { gemini: null, claude: null };
const EMPTY_LOADINGS  = { gemini: false, claude: false };

const SESSION_KEY = 'practice_session';

const API_BASE = process.env.REACT_APP_API_URL;

/** Swap the mark digit in an ID: cyc-6 ↔ cyc-9, haz-6a ↔ haz-9a, etc. */
function getPairedId(currentId, targetType) {
  if (targetType === '9-mark') return currentId.replace(/-6([a-z]*)$/, '-9$1');
  return currentId.replace(/-9([a-z]*)$/, '-6$1');
}

/** Decode a JWT payload without verification (claims inspection only) */
function decodeJwtPayload(token) {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
}

/** Load session from sessionStorage, return empty object on failure */
function loadSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function App() {
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently, user } = useAuth0();

  const [examLevel, setExamLevel]                 = useState('a-level'); // 'a-level' | 'gcse'
  const [aLevelSubject, setALevelSubject]         = useState('geography'); // 'geography' | 'env-science'
  const [gcseSubject, setGcseSubject]             = useState('biology');
  const [selectedArchetype, setSelectedArchetype] = useState('outline_4');
  const [markType, setMarkType]                   = useState('6-mark');
  const [activeProvider, setActiveProvider]       = useState('claude');
  const [selectedQuestion, setSelectedQuestion]   = useState(null);
  const [steps, setSteps]                         = useState(EMPTY_STEPS);
  const [feedbacks, setFeedbacks]                 = useState(EMPTY_FEEDBACKS);
  const [errors, setErrors]                       = useState(EMPTY_ERRORS);
  const [loadings, setLoadings]                   = useState(EMPTY_LOADINGS);
  // Mobile panel navigation: which panel is visible on narrow screens
  // 'sidebar' | 'answer' | 'feedback'
  const [mobilePanel, setMobilePanel]             = useState('sidebar');
  const [timerElapsed, setTimerElapsed]           = useState(0);
  const [timerActive, setTimerActive]             = useState(false);
  const [isAdmin, setIsAdmin]                     = useState(false);
  const [showDashboard, setShowDashboard]         = useState(false);
  const [mockExamOpen, setMockExamOpen]           = useState(false);
  const [examHistoryOpen, setExamHistoryOpen]     = useState(false);
  const [historyOpen, setHistoryOpen]             = useState(false);
  const [quizOpen, setQuizOpen]                   = useState(false);
  const [leaderboardOpen, setLeaderboardOpen]     = useState(false);
  const [scoreReviewOpen, setScoreReviewOpen]     = useState(false);
  const [isTyping, setIsTyping]                   = useState(false);
  const [guidedMode, setGuidedMode]               = useState(true);
  const [mcqResult, setMcqResult]                 = useState(null);
  const [loadingBreakdowns, setLoadingBreakdowns] = useState(EMPTY_LOADINGS);
  const [totalPoints, setTotalPoints]             = useState(null);
  const [currentStreak, setCurrentStreak]         = useState(0);
  const [todayPoints, setTodayPoints]             = useState(null);
  const [questionBestScores, setQuestionBestScores] = useState({});

  // Score review data (fetched when scoreReviewOpen becomes true)
  const [scoreSummary, setScoreSummary]           = useState(null);
  const [scoreDailyData, setScoreDailyData]       = useState(null);
  const [scoreHistory, setScoreHistory]           = useState([]);
  const [scoreHistoryTotal, setScoreHistoryTotal] = useState(0);
  const [scoreHistoryOffset, setScoreHistoryOffset] = useState(0);
  const [scoreHistoryFilter, setScoreHistoryFilter] = useState('all');
  const [scoreLoading, setScoreLoading]           = useState(false);

  // Admin: viewing a specific student's score
  const [adminStudentScore, setAdminStudentScore] = useState(null); // { userId, displayName, summary, dailyData }

  // ─── Session scratchpad ──────────────────────────────────────────────────────
  // Shape: { [questionId]: { steps, feedbacks, isMarked } }
  // Backed by sessionStorage so page-refresh preserves drafts.
  // Cleared automatically when the user logs out (isAuthenticated → false).
  const [sessionAnswers, setSessionAnswers] = useState(loadSession);

  // Stable refs so callbacks can always see the latest values without being
  // recreated on every render.
  const sessionAnswersRef    = useRef(sessionAnswers);
  const selectedQuestionRef  = useRef(selectedQuestion);
  const activeProviderRef    = useRef(activeProvider);
  const selectedArchetypeRef = useRef(selectedArchetype);
  const timerStartRef        = useRef(null);
  const inactivityRef        = useRef(null);

  useEffect(() => { sessionAnswersRef.current    = sessionAnswers;    }, [sessionAnswers]);
  useEffect(() => { selectedQuestionRef.current  = selectedQuestion;  }, [selectedQuestion]);
  useEffect(() => { activeProviderRef.current    = activeProvider;    }, [activeProvider]);
  useEffect(() => { selectedArchetypeRef.current = selectedArchetype; }, [selectedArchetype]);

  // Persist session to sessionStorage on every change
  useEffect(() => {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionAnswers)); } catch { /* quota full */ }
  }, [sessionAnswers]);

  // Clear session on logout
  useEffect(() => {
    if (!isAuthenticated) {
      sessionStorage.removeItem(SESSION_KEY);
      setSessionAnswers({});
    }
  }, [isAuthenticated]);

  // IDs of questions the student has successfully marked this session
  const markedQuestionIds = useMemo(
    () => new Set(Object.entries(sessionAnswers).filter(([, v]) => v?.isMarked).map(([k]) => k)),
    [sessionAnswers],
  );

  // The question pool changes with exam level / subject selection
  const activeQuestions = examLevel === 'gcse'
    ? (gcseQuestions[gcseSubject] ?? [])
    : (aLevelSubject === 'geography' ? questions : envSciQuestions);

  // Switch exam level: clear question and answer state, reset archetype default
  const handleExamLevelChange = useCallback((level) => {
    setExamLevel(level);
    setSelectedQuestion(null);
    setSteps(EMPTY_STEPS);
    setFeedbacks(EMPTY_FEEDBACKS);
    setErrors(EMPTY_ERRORS);
    setSelectedArchetype(level === 'gcse' ? 'past_papers' : 'outline_4');
    setMobilePanel('sidebar');
  }, []);

  const handleGcseSubjectChange = useCallback((subject) => {
    setGcseSubject(subject);
    setSelectedQuestion(null);
    setSteps(EMPTY_STEPS);
    setFeedbacks(EMPTY_FEEDBACKS);
    setErrors(EMPTY_ERRORS);
    setSelectedArchetype('past_papers');
  }, []);

  const handleALevelSubjectChange = useCallback((subject) => {
    setALevelSubject(subject);
    setSelectedQuestion(null);
    setSteps(EMPTY_STEPS);
    setFeedbacks(EMPTY_FEEDBACKS);
    setErrors(EMPTY_ERRORS);
    setSelectedArchetype(subject === 'env-science' ? 'envsci_short' : 'outline_4');
  }, []);

  // For scaffold_9, override the question's own archetype so AnswerPanel renders
  // the scaffold layout regardless of the underlying question's archetype tag.
  const archetype     = selectedArchetype === 'scaffold_9'
    ? 'scaffold_9'
    : (selectedQuestion?.archetype ?? null);
  const isLoading     = loadings[activeProvider];
  const hasBeenMarked = feedbacks.gemini !== null || feedbacks.claude !== null;
  const hasResource   = selectedQuestion?.resource?.type === 'multi-resource'
    && selectedQuestion?.resource?.figures?.length > 0;

  // ─── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!timerActive) return;
    const id = setInterval(() => {
      setTimerElapsed(Math.floor((Date.now() - timerStartRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [timerActive]);

  // ─── Heartbeat + typing activity tracking ───────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    let localIsTyping = false;

    async function sendHeartbeat() {
      try {
        const token = await getAccessTokenSilently();

        // Decode roles from the access token on first successful fetch
        if (!isAdmin) {
          const payload = decodeJwtPayload(token);
          const roles = payload['https://tutor-app/roles'] ?? [];
          if (roles.includes('admin')) setIsAdmin(true);
        }

        await fetch(`${API_BASE}/api/heartbeat`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: user?.email }),
        });
      } catch {
        // Silently ignore heartbeat failures
      }
    }

    function resetInactivityTimer() {
      clearTimeout(inactivityRef.current);
      inactivityRef.current = setTimeout(() => {
        localIsTyping = false;
        setIsTyping(false);
      }, 20_000);
    }

    function handleKeyDown() {
      if (!localIsTyping) {
        localIsTyping = true;
        setIsTyping(true);
        sendHeartbeat();
      }
      resetInactivityTimer();
    }

    sendHeartbeat();

    const intervalId = setInterval(() => {
      if (localIsTyping) sendHeartbeat();
    }, 20_000);

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearInterval(intervalId);
      clearTimeout(inactivityRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, getAccessTokenSilently]);

  // ─── Points summary fetch (periodic) ─────────────────────────────────────────
  const fetchPointsSummary = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/points/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTotalPoints((data.totalPoints || 0) + (data.bonusPoints || 0));
      setCurrentStreak(data.currentStreakDays || 0);
      setTodayPoints(data.todayPoints ?? 0);
      // Confetti trigger
      if (data.shouldConfetti) {
        confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
      }
    } catch {
      // Silently ignore
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchPointsSummary();
    const id = setInterval(fetchPointsSummary, 60_000); // refresh every minute
    return () => clearInterval(id);
  }, [isAuthenticated, fetchPointsSummary]);

  // ─── Score review data fetch ─────────────────────────────────────────────────
  const fetchScoreData = useCallback(async (historyOffset = 0) => {
    if (!isAuthenticated) return;
    if (historyOffset === 0) setScoreLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const [summaryRes, dailyRes, historyRes] = await Promise.all([
        fetch(`${API_BASE}/api/points/summary`, { headers }),
        fetch(`${API_BASE}/api/points/daily`, { headers }),
        fetch(`${API_BASE}/api/points/history?limit=100&offset=${historyOffset}`, { headers }),
      ]);
      const [summaryData, dailyData, historyData] = await Promise.all([
        summaryRes.json(),
        dailyRes.json(),
        historyRes.json(),
      ]);
      setScoreSummary(summaryData);
      setScoreDailyData(dailyData);
      setScoreHistory(prev => historyOffset === 0 ? (historyData.entries || []) : [...prev, ...(historyData.entries || [])]);
      setScoreHistoryTotal(historyData.total || 0);
      setScoreHistoryOffset(historyOffset);
    } catch (e) {
      console.error('Score data fetch error:', e);
    } finally {
      setScoreLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (scoreReviewOpen) {
      setScoreHistoryFilter('all');
      fetchScoreData(0);
    }
  }, [scoreReviewOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Admin: view a student's score ──────────────────────────────────────────
  const handleAdminStudentClick = useCallback(async ({ userId, displayName }) => {
    setAdminStudentScore({ userId, displayName, summary: null, dailyData: null, loading: true });
    setLeaderboardOpen(false);
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const [summaryRes, dailyRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/students/${userId}/points/summary`, { headers }),
        fetch(`${API_BASE}/api/admin/students/${userId}/points/daily`, { headers }),
      ]);
      const [summary, dailyData] = await Promise.all([summaryRes.json(), dailyRes.json()]);
      setAdminStudentScore({ userId, displayName, summary, dailyData, loading: false });
    } catch (e) {
      console.error('Admin student score fetch error:', e);
      setAdminStudentScore(prev => prev ? { ...prev, loading: false } : null);
    }
  }, [getAccessTokenSilently]);

  // ─── Practice history — best scores per question ─────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`${API_BASE}/api/my-history/practice`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const scores = {};
        for (const attempt of data.attempts || []) {
          const existing = scores[attempt.questionId];
          if (existing === undefined || attempt.score > existing) {
            scores[attempt.questionId] = attempt.score;
          }
        }
        setQuestionBestScores(scores);
      } catch { /* silent — scores just won't show */ }
    })();
  }, [isAuthenticated, getAccessTokenSilently]);

  // ─── Question navigation ─────────────────────────────────────────────────────
  const handleMarkTypeChange = useCallback((newType) => {
    setMarkType(newType);
    setSteps(EMPTY_STEPS);
    setFeedbacks(EMPTY_FEEDBACKS);
    setErrors(EMPTY_ERRORS);
    if (selectedQuestionRef.current) {
      const pairedId = getPairedId(selectedQuestionRef.current.id, newType);
      const paired = questions.find((q) => q.id === pairedId) ?? null;
      setSelectedQuestion(paired);
    }
  }, []);

  // Restore steps + feedback from the session scratchpad when switching questions
  const handleQuestionSelect = useCallback((question) => {
    setSelectedQuestion(question);
    const saved = sessionAnswersRef.current[question.id];
    const arch  = question.archetype;
    const cardBased = ['outline_4', 'assess_6', 'scaffold_9', 'essay_20', 'envsci_9', 'envsci_25', 'gcse_science_answer', 'gcse_science_extended_6'].includes(arch);
    setSteps(saved?.steps ?? (cardBased ? emptySteps(arch) : EMPTY_STEPS));
    setFeedbacks(saved?.feedbacks ?? EMPTY_FEEDBACKS);
    setErrors(EMPTY_ERRORS);
    setLoadings(EMPTY_LOADINGS);
    setMcqResult(null);
    timerStartRef.current = Date.now();
    setTimerElapsed(0);
    setTimerActive(true);
    // On mobile, auto-switch to the answer panel; close leaderboard if open
    setLeaderboardOpen(false);
    setMobilePanel('answer');
  }, []);

  // Sync typed content to the session scratchpad in real time
  const handleStepChange = useCallback((stepId, value) => {
    const qId = selectedQuestionRef.current?.id;
    setSteps((prev) => ({ ...prev, [stepId]: value }));
    if (qId) {
      setSessionAnswers((sa) => ({
        ...sa,
        [qId]: { ...(sa[qId] ?? {}), steps: { ...(sa[qId]?.steps ?? {}), [stepId]: value } },
      }));
    }
  }, []);

  // ─── Marking ─────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    const question = selectedQuestionRef.current;
    const provider = activeProviderRef.current;
    if (!question || loadings[provider]) return;

    // Use selectedArchetypeRef to handle scaffold_9 override without stale closure
    const arch = selectedArchetypeRef.current === 'scaffold_9' ? 'scaffold_9' : question.archetype;
    let hasContent;
    if (
      arch === 'outline_4' || arch === 'assess_6' || arch === 'scaffold_9' || arch === 'essay_20' ||
      arch === 'envsci_9' || arch === 'envsci_25' ||
      arch === 'gcse_science_answer' || arch === 'gcse_science_extended_6'
    ) {
      hasContent = Object.values(steps).every((v) => v.trim().length > 0);
    } else {
      hasContent = Object.values(steps).some((v) => v.trim().length > 0);
    }
    if (!hasContent) return;

    setTimerActive(false);
    setTimerElapsed(Math.floor((Date.now() - timerStartRef.current) / 1000));
    setLoadings((prev) => ({ ...prev, [provider]: true }));
    setFeedbacks((prev) => ({ ...prev, [provider]: null }));
    setErrors((prev) => ({ ...prev, [provider]: null }));
    setLoadingBreakdowns((prev) => ({ ...prev, [provider]: false }));
    // On mobile, switch to the feedback panel to show marking progress
    setMobilePanel('feedback');

    // scaffold_9: two sequential calls — show score after call 1, breakdown after call 2
    if (arch === 'scaffold_9') {
      try {
        const token   = await getAccessTokenSilently();
        const elapsed = Math.floor((Date.now() - timerStartRef.current) / 1000);

        // ── Call 1: score + justification ──────────────────────────────────────
        const result = await markScaffold9(question, steps, provider, token, elapsed);
        setFeedbacks((prev) => ({ ...prev, [provider]: { ...result, isPartial: true } }));
        setLoadings((prev) => ({ ...prev, [provider]: false }));

        // ── Call 2: paragraph breakdown ────────────────────────────────────────
        setLoadingBreakdowns((prev) => ({ ...prev, [provider]: true }));
        try {
          const breakdown = await markScaffold9Breakdown(
            result.attemptId,
            question,
            steps,
            provider,
            { awarded_mark: result.total ?? result.awarded_mark, awarded_level: result.level ?? result.awarded_level },
            token,
          );
          const fullFeedback = { ...result, ...breakdown, isPartial: false };
          setFeedbacks((prev) => ({ ...prev, [provider]: fullFeedback }));
          const qId = question.id;
          setSessionAnswers((sa) => ({
            ...sa,
            [qId]: {
              ...(sa[qId] ?? {}),
              feedbacks: { ...(sa[qId]?.feedbacks ?? EMPTY_FEEDBACKS), [provider]: fullFeedback },
              isMarked: true,
            },
          }));
          const newScore = fullFeedback.total ?? fullFeedback.awarded_mark;
          if (newScore != null) {
            setQuestionBestScores((prev) => ({
              ...prev,
              [qId]: Math.max(prev[qId] ?? 0, newScore),
            }));
          }
        } catch (bdErr) {
          // Breakdown failed — score is still shown, breakdown section shows error state
          console.error('Scaffold9 breakdown failed:', bdErr);
          setFeedbacks((prev) => ({
            ...prev,
            [provider]: { ...(prev[provider] ?? {}), isPartial: false, breakdownError: true },
          }));
        } finally {
          setLoadingBreakdowns((prev) => ({ ...prev, [provider]: false }));
        }
      } catch (err) {
        setErrors((prev) => ({ ...prev, [provider]: err.message || 'An unexpected error occurred.' }));
        setLoadings((prev) => ({ ...prev, [provider]: false }));
      }
      return;
    }

    // essay_20: two sequential calls — show score after call 1, breakdown after call 2
    if (arch === 'essay_20') {
      try {
        const token   = await getAccessTokenSilently();
        const elapsed = Math.floor((Date.now() - timerStartRef.current) / 1000);

        // ── Call 1: score + justification ──────────────────────────────────────
        const result = await markEssay20(question, steps, provider, token, elapsed);
        setFeedbacks((prev) => ({ ...prev, [provider]: { ...result, isPartial: true } }));
        setLoadings((prev) => ({ ...prev, [provider]: false }));

        // ── Call 2: paragraph breakdown ────────────────────────────────────────
        setLoadingBreakdowns((prev) => ({ ...prev, [provider]: true }));
        try {
          const breakdown = await markEssay20Breakdown(
            result.attemptId,
            question,
            steps,
            provider,
            { awarded_mark: result.total ?? result.awarded_mark, awarded_level: result.level ?? result.awarded_level },
            token,
          );
          const fullFeedback = { ...result, ...breakdown, isPartial: false };
          setFeedbacks((prev) => ({ ...prev, [provider]: fullFeedback }));
          const qId = question.id;
          setSessionAnswers((sa) => ({
            ...sa,
            [qId]: {
              ...(sa[qId] ?? {}),
              feedbacks: { ...(sa[qId]?.feedbacks ?? EMPTY_FEEDBACKS), [provider]: fullFeedback },
              isMarked: true,
            },
          }));
          const newScore = fullFeedback.total ?? fullFeedback.awarded_mark;
          if (newScore != null) {
            setQuestionBestScores((prev) => ({
              ...prev,
              [qId]: Math.max(prev[qId] ?? 0, newScore),
            }));
          }
        } catch (bdErr) {
          console.error('Essay20 breakdown failed:', bdErr);
          setFeedbacks((prev) => ({
            ...prev,
            [provider]: { ...(prev[provider] ?? {}), isPartial: false, breakdownError: true },
          }));
        } finally {
          setLoadingBreakdowns((prev) => ({ ...prev, [provider]: false }));
        }
      } catch (err) {
        setErrors((prev) => ({ ...prev, [provider]: err.message || 'An unexpected error occurred.' }));
        setLoadings((prev) => ({ ...prev, [provider]: false }));
      }
      return;
    }

    try {
      const token   = await getAccessTokenSilently();
      const elapsed = Math.floor((Date.now() - timerStartRef.current) / 1000);

      const result =
        arch === 'outline_4' ? await markOutline4(question, steps, provider, token, elapsed)
        : arch === 'assess_6' ? await markAssess6(question, steps, provider, token, elapsed)
        : arch === 'gcse_science_answer' || arch === 'gcse_science_extended_6'
            ? await markOutline4(question, steps, provider, token, elapsed)
        : await markAnswer(question, steps, markType, token, elapsed);

      // Update visible feedback
      setFeedbacks((prev) => ({ ...prev, [provider]: result }));

      // Persist result into the session scratchpad and flag as marked
      const qId = question.id;
      setSessionAnswers((sa) => ({
        ...sa,
        [qId]: {
          ...(sa[qId] ?? {}),
          feedbacks: { ...(sa[qId]?.feedbacks ?? EMPTY_FEEDBACKS), [provider]: result },
          isMarked: true,
        },
      }));
      const newScore = result.total ?? result.awarded_mark;
      if (newScore != null) {
        setQuestionBestScores((prev) => ({
          ...prev,
          [qId]: Math.max(prev[qId] ?? 0, newScore),
        }));
      }
    } catch (err) {
      setErrors((prev) => ({ ...prev, [provider]: err.message || 'An unexpected error occurred.' }));
    } finally {
      setLoadings((prev) => ({ ...prev, [provider]: false }));
    }
  }, [steps, markType, loadings, getAccessTokenSilently]);

  // ─── Clear actions ───────────────────────────────────────────────────────────
  const handleClear = useCallback(() => {
    const provider = activeProviderRef.current;
    const qId      = selectedQuestionRef.current?.id;
    const arch     = selectedArchetypeRef.current === 'scaffold_9' ? 'scaffold_9' : selectedQuestionRef.current?.archetype;
    const empty    = ['outline_4', 'assess_6', 'scaffold_9', 'essay_20', 'envsci_9', 'envsci_25', 'gcse_science_answer', 'gcse_science_extended_6'].includes(arch) ? emptySteps(arch) : EMPTY_STEPS;

    setFeedbacks((prev) => ({ ...prev, [provider]: null }));
    setErrors((prev) => ({ ...prev, [provider]: null }));
    setSteps(empty);

    if (qId) {
      setSessionAnswers((sa) => ({
        ...sa,
        [qId]: {
          ...(sa[qId] ?? {}),
          steps: empty,
          feedbacks: { ...(sa[qId]?.feedbacks ?? EMPTY_FEEDBACKS), [provider]: null },
        },
      }));
    }
  }, []);

  const handleClearAll = useCallback(() => {
    const qId   = selectedQuestionRef.current?.id;
    const arch  = selectedArchetypeRef.current === 'scaffold_9' ? 'scaffold_9' : selectedQuestionRef.current?.archetype;
    const empty = ['outline_4', 'assess_6', 'scaffold_9', 'essay_20', 'envsci_9', 'envsci_25', 'gcse_science_answer', 'gcse_science_extended_6'].includes(arch) ? emptySteps(arch) : EMPTY_STEPS;

    setSteps(empty);
    setFeedbacks(EMPTY_FEEDBACKS);
    setErrors(EMPTY_ERRORS);

    if (qId) {
      setSessionAnswers((sa) => ({
        ...sa,
        [qId]: { steps: empty, feedbacks: EMPTY_FEEDBACKS, isMarked: false },
      }));
    }
  }, []);

  // ─── Render guards ───────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="h-screen bg-[#0d1117] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="dark h-screen bg-[#0d1117] flex overflow-hidden relative">

      {/* ── Mobile bottom navigation bar ──────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-[#0d1117] border-t border-[#30363d] flex">
        <button
          onClick={() => setMobilePanel('sidebar')}
          className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-mono transition-colors ${
            mobilePanel === 'sidebar'
              ? 'text-[#58a6ff] bg-[#58a6ff]/5'
              : 'text-[#8b949e] hover:text-[#e6edf3]'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
          </svg>
          Questions
        </button>
        <button
          onClick={() => setMobilePanel('answer')}
          className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-mono transition-colors ${
            mobilePanel === 'answer'
              ? 'text-[#58a6ff] bg-[#58a6ff]/5'
              : 'text-[#8b949e] hover:text-[#e6edf3]'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Answer
        </button>
        <button
          onClick={() => setMobilePanel('feedback')}
          className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-mono transition-colors ${
            mobilePanel === 'feedback'
              ? hasResource && !hasBeenMarked && !isLoading ? 'text-[#58a6ff] bg-[#58a6ff]/5' : 'text-[#3fb950] bg-[#3fb950]/5'
              : hasResource && !hasBeenMarked && !isLoading ? 'text-[#58a6ff] hover:text-[#58a6ff]'
              : feedbacks[activeProvider] ? 'text-[#3fb950] hover:text-[#3fb950]'
              : loadings[activeProvider] ? 'text-[#d29922] animate-pulse'
              : 'text-[#8b949e] hover:text-[#e6edf3]'
          }`}
        >
          {hasResource && !hasBeenMarked && !isLoading ? (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {loadings[activeProvider] ? 'Marking…' : hasResource && !hasBeenMarked && !isLoading ? 'Resource' : 'Feedback'}
        </button>
      </div>

      {/* ── Sidebar ───────────────────────────────────────────────────── */}
      <div className={`
        flex-shrink-0 h-screen overflow-hidden transition-all duration-200
        ${mobilePanel === 'sidebar' ? 'mobile-panel-visible' : 'mobile-panel-hidden'}
        lg:!block lg:w-72
        pb-14 lg:pb-0
      `}>
        <Sidebar
          selectedQuestion={selectedQuestion}
          onSelect={handleQuestionSelect}
          markType={markType}
          onMarkTypeChange={handleMarkTypeChange}
          selectedArchetype={selectedArchetype}
          onArchetypeChange={(a) => {
            setSelectedArchetype(a);
            setSelectedQuestion(null);
            setFeedbacks(EMPTY_FEEDBACKS);
            setErrors(EMPTY_ERRORS);
          }}
          activeProvider={activeProvider}
          onProviderChange={setActiveProvider}
          isAdmin={isAdmin}
          onOpenDashboard={() => setShowDashboard(true)}
          onOpenMockExam={() => setMockExamOpen(true)}
          onOpenExamHistory={() => setExamHistoryOpen(true)}
          onOpenHistory={() => setHistoryOpen(true)}
          onOpenQuiz={() => { setLeaderboardOpen(false); setQuizOpen(true); }}
          markedQuestionIds={markedQuestionIds}
          isTyping={isTyping}
          guidedMode={guidedMode}
          onGuidedModeChange={setGuidedMode}
          onOpenLeaderboard={() => { setLeaderboardOpen(true); setMobilePanel('answer'); }}
          onOpenScoreReview={() => setScoreReviewOpen(true)}
          totalPoints={totalPoints}
          todayPoints={todayPoints}
          currentStreak={currentStreak}
          questionBestScores={questionBestScores}
          examLevel={examLevel}
          onExamLevelChange={handleExamLevelChange}
          aLevelSubject={aLevelSubject}
          onALevelSubjectChange={handleALevelSubjectChange}
          gcseSubject={gcseSubject}
          onGcseSubjectChange={handleGcseSubjectChange}
          activeQuestions={activeQuestions}
        />
      </div>

      {/* ── Main content ──────────────────────────────────────────────── */}
      <main className={`flex-1 flex min-w-0 h-screen overflow-hidden ${
        mobilePanel === 'sidebar' ? 'mobile-panel-hidden' : ''
      } lg:!flex`}>
        <div className={`flex-1 flex flex-col min-w-0 ${
          mobilePanel === 'answer' ? 'mobile-panel-visible' : 'mobile-panel-hidden'
        } lg:!flex pb-14 lg:pb-0`}>
          {adminStudentScore ? (
            <ScoreCenter
              summary={adminStudentScore.summary}
              dailyData={adminStudentScore.dailyData}
              loading={adminStudentScore.loading}
              title={adminStudentScore.displayName}
              onClose={() => setAdminStudentScore(null)}
            />
          ) : leaderboardOpen ? (
            <Leaderboard
              onClose={() => setLeaderboardOpen(false)}
              getAccessToken={getAccessTokenSilently}
              isAdmin={isAdmin}
              onStudentClick={handleAdminStudentClick}
            />
          ) : scoreReviewOpen ? (
            <ScoreCenter
              summary={scoreSummary}
              dailyData={scoreDailyData}
              loading={scoreLoading}
              onClose={() => setScoreReviewOpen(false)}
            />
          ) : (
            <AnswerPanel
              question={selectedQuestion}
              steps={steps}
              onChange={handleStepChange}
              onSubmit={handleSubmit}
              onClearAll={selectedQuestion ? handleClearAll : undefined}
              isLoading={isLoading}
              hasBeenMarked={hasBeenMarked}
              markType={markType}
              archetype={archetype}
              timerElapsed={timerElapsed}
              timerActive={timerActive}
              guidedMode={guidedMode}
              onMcqAnswer={(result) => {
                setMcqResult(result);
                setMobilePanel('feedback');
              }}
            />
          )}
        </div>
        <div className={`w-96 flex-shrink-0 flex flex-col ${
          mobilePanel === 'feedback' ? 'mobile-panel-visible' : 'mobile-panel-hidden'
        } lg:!flex lg:!w-96 pb-14 lg:pb-0`}>
          {scoreReviewOpen ? (
            <ScoreHistory
              history={scoreHistory}
              historyTotal={scoreHistoryTotal}
              historyOffset={scoreHistoryOffset}
              filter={scoreHistoryFilter}
              onFilterChange={setScoreHistoryFilter}
              onLoadMore={() => fetchScoreData(scoreHistoryOffset + 100)}
              loading={scoreLoading}
            />
          ) : hasResource && !hasBeenMarked && !isLoading ? (
            <ResourceViewer resource={selectedQuestion.resource} revealCaption={false} />
          ) : (
            <FeedbackTerminal
              feedbacks={feedbacks}
              errors={errors}
              loadings={loadings}
              loadingBreakdowns={loadingBreakdowns}
              activeProvider={activeProvider}
              onClear={handleClear}
              markType={markType}
              archetype={archetype}
              steps={steps}
              timerElapsed={timerElapsed}
              getToken={getAccessTokenSilently}
              resource={hasResource ? selectedQuestion?.resource : null}
              mcqResult={mcqResult}
              questionMarks={selectedQuestion?.marks}
            />
          )}
        </div>
      </main>

      {/* Usage Dashboard overlay */}
      {showDashboard && (
        <UsageDashboard onClose={() => setShowDashboard(false)} />
      )}

      {/* Mock Exam overlay */}
      {mockExamOpen && (
        <MockExamOrchestrator
          onClose={() => setMockExamOpen(false)}
          getToken={getAccessTokenSilently}
          subject={examLevel === 'gcse' ? gcseSubject : aLevelSubject}
        />
      )}

      {/* Exam History overlay */}
      {examHistoryOpen && (
        <ExamHistoryOrchestrator
          onClose={() => setExamHistoryOpen(false)}
          getToken={getAccessTokenSilently}
        />
      )}

      {/* History Hub overlay (Practice + Exam tabs) */}
      {historyOpen && (
        <HistoryHub onClose={() => setHistoryOpen(false)} />
      )}

      {/* MCQ Quiz overlay */}
      {quizOpen && (
        <QuizOrchestrator onClose={() => setQuizOpen(false)} onPointsRefresh={fetchPointsSummary} examLevel={examLevel} gcseSubject={gcseSubject} aLevelSubject={aLevelSubject} />
      )}

    </div>
  );
}
